/* Per-Account permissions the Dashboard needs: whether the person may see an
   Account's money (owner or Stats access) and switch its AI (owner, or any of the
   account-update features — backend ACCOUNT_UPDATE_FEATURES). Super admins have
   everything without asking; others' permissions are read a few at a time. */

import { useEffect, useRef } from 'react';
import type { Account, AccountPermissions } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { hasFeatureAccess, isAccountOwner, type FeatureName } from '../permissions';

const UPDATE_FEATURES: FeatureName[] = ['assistant_setup', 'assistant_content', 'model_info', 'notifications_settings'];
const PARALLEL = 4;

export const canSeeMoney = (p: AccountPermissions | undefined) => isAccountOwner(p) || hasFeatureAccess(p, 'stats');
export const canSwitchAi = (p: AccountPermissions | undefined) => isAccountOwner(p) || UPDATE_FEATURES.some((f) => hasFeatureAccess(p, f));

export function useAccountPermissions(accounts: Account[]) {
  const { isSuperAdmin, getPermissions, loadAccountPermissions } = useAuth();
  const requested = useRef(new Set<string>());
  const loadRef = useRef(loadAccountPermissions);
  loadRef.current = loadAccountPermissions;

  const missing = isSuperAdmin ? [] : accounts.filter((a) => !getPermissions(a.account_id) && !requested.current.has(a.account_id)).map((a) => a.account_id);
  const missingKey = missing.join(',');

  useEffect(() => {
    if (!missingKey) return;
    const queue = missingKey.split(',');
    queue.forEach((id) => requested.current.add(id));
    // Not cancelled on re-render: every Account asked for once gets its answer.
    const worker = async () => {
      while (queue.length) {
        const id = queue.shift();
        if (id) await loadRef.current(id).catch(() => {});
      }
    };
    for (let i = 0; i < PARALLEL; i += 1) void worker();
  }, [missingKey]);

  const known = isSuperAdmin || accounts.every((a) => !!getPermissions(a.account_id));
  return { known, getPermissions };
}
